export interface AddOnOffer {
  cohortId?: number;
  nextPath?: string;
  orderItem: OrderItem;
  orderUrl: string;
  paymentDetails: {
    cardBrand: string;
    cardLast4: string;
    paymentMethod: string;
    stripePaymentMethodId: string;
  };
  price: {
    couponCode: string;
    discountedPrice: number;
    fullPrice: number;
  };
  stripeToken: string;
  successPath?: string;
}

export interface Coupon {
  code: string;
  discountDescription: string;
  discountedPrice: number;
  fullPrice: number;
}

export interface SubscriptionPlan {
  cohortId: number;
  currency: string;
  description: string;
  durationDays: number;
  durationMonths: number;
  id: number;
  initialAmount?: number;
  installmentInterval?: 'week' | 'month';
  installmentIntervalCount?: number;
  name: string;
  planSlug: string;
  priceCents: number;
  slug: string;
}

export interface BulkSelfServeFormProps {
  nonprofit: boolean;
  subscriptionPlans: SubscriptionPlan[];
}

export interface SubscriptionFormProps {
  abTest?: {
    test: string;
    version: string;
  };
  allowInstallments: boolean;
  bulkOrder: boolean;
  discount?: Discount;
  isEuUser: boolean;
  pageName: string;
  signedIn: boolean;
  signupMotivators?: boolean;
  stripeToken?: string;
  subscriptionPlans: SubscriptionPlan[];
  twoStepSignup: boolean;
  user?: {
    email: string;
    password: string;
    passwordConfirmation: string;
    termsAndPolicyAccepted: boolean;
  };
}

export interface OrderItem {
  priceCentsPerItem: number;
  purchasable: SubscriptionPlan;
  purchasableCount: number;
}

export interface BulkSelfServeFormValues {
  orderItems: OrderItem[];
}

export interface SubscriptionFormValues {
  _error?: string;
  alipayPaymentUrl?: string; // Used for redirecting to Alipay payment page
  cardCvc?: string;
  cardExpirationDateMonth?: number;
  cardExpirationDateYear?: number;
  cardName?: string;
  cardNumber?: string;
  cardZipCode?: string;
  cohortId?: number;
  couponCode?: string;
  discount?: Discount;
  email?: string;
  orderItems: OrderItem[];
  password?: string;
  passwordConfirmation?: string;
  paymentMethod: PaymentMethod;
  paymentStyle?: PaymentStyle;
  purchaseToken?: string;
  signedIn: boolean;
  stripePaymentMethod?: any;
  termsAndPolicyAccepted: boolean;
}

export interface InstallmentsFormGroupProps {
  formattedDiscountPrice: string;
  formattedInstallmentsDates: string[];
  formattedInstallmentPrice: string;
  setFieldValue: (field: string, value: any) => void;
  values: SubscriptionFormValues;
}

export interface PaymentMethodsFormGroupProps {
  allowedPaymentMethods?: PaymentMethod[];
  formattedDiscountPrice: string;
  paymentRequestMethod?: string;
  values: SubscriptionFormValues;
}

export interface SingleConfirmationFormGroupProps {
  formattedDiscountPrice: string;
  formattedExpirationDate: string;
  formattedInstallmentPrice: string;
  formattedPrice: string;
  setFieldValue: (field: string, value: any) => void;
  values: SubscriptionFormValues;
}

export interface MultiConfirmationFormGroupProps {
  className?: string;
  errors?: any;
  requirePersonalizedPackage: boolean;
  values: BulkSelfServeFormValues;
  variant?: string;
}

export enum PaymentStyle {
  INSTALLMENTS = 'INSTALLMENTS',
  FULLPAYMENT = 'FULLPAYMENT'
}

export enum PaymentMethod {
  ALIPAY = 'stripe_source',
  CREDITCARD = 'credit_card',
  PAYPAL = 'paypal_express',
  STRIPE = 'stripe',
  STRIPE_PAYMENT_REQUEST = 'stripe_payment_request'
}

export interface Discount {
  compoundPrice?: CompoundPrice[];
  couponCode: string;
  defaultCoupon?: {
    couponCode: string;
    discountAmount: number;
  };
  discountDescription: string;
  discountedPrice: number;
  fullPrice: number;
  upgrade: boolean;
}

export interface CompoundPrice {
  fullPrice?: number;
  name: string;
  price: number;
}

export interface SubscriptionSubmissionResponse {
  _redirect?: string;
  actionRequired?: 'confirmSetup' | 'confirmPayment' | 'completePaymentRequest';
  amount?: number;
  clientSecret?: string;
  nextStepUrl?: string;
  paymentMethodId?: string;
}
